import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, Inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogModule, MatDialogTitle, MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { CONSTANTS } from "../../../../Constants/Constants";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AdvancedOptions } from "../../../../models/dtos/Classes/AdvancedOptions";
import { DistributeTopLevelOptions } from "../../../../models/dtos/Enums/DistributeTopLevelOptions";
import { EstimateStatus } from "../../../../models/dtos/Enums/EstimateStatus";
import { UnlinkProposalRequest } from "../../../../models/dtos/Classes/UnlinkProposalRequest";
import { IAdvancedOptionsFormGroup } from "../../../../models/properties/IAdvancedOptionsFormGroup";
import { CommonDataService } from "../../../../services/common-data.service";
import { ConfirmationDialogService } from "../../../../services/common/confirmation-dialog.service";
import { NotificationMessageService } from "../../../../services/common/notification-message.service";
import { OpenEstimateApiService } from "../../../../services/api/open-estimate.service";
import { CloseOnEscapeDirective } from "../../../../directives/close-on-escape.directive";
import { CommonMetaDataService } from "../../../../services/common-meta-data.service";
import { CommonFunctionsService } from "../../../../services/common-functions.service";
import { EstimateCommonFunctionsService } from "../../../../services/estimate/estimate-common-functions.service";

@Component({
    selector: 'app-sol-properties-proposal-advanced-options',
    templateUrl: './sol-properties-proposal-advanced-options.component.html',
    styleUrls: ['./sol-properties-proposal-advanced-options.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatRadioModule,
        MatCheckboxModule,
        MatSelectModule,
        TranslatePipe,
        CloseOnEscapeDirective
    ]
})
export class SolPropertiesProposalAdvancedOptionsComponent implements OnInit {
    advancedOptionsForm: FormGroup<IAdvancedOptionsFormGroup> 
    distributeTopLevelOptions : DistributeTopLevelOptions
    private destroyRef = inject(DestroyRef);

    areas:{id:string, description:string}[] = [];
    advancedOptions: AdvancedOptions;
    isProposalCanBeUnlinked = false;
    isSDIApplicable: boolean = false;

    constructor(
        private readonly fb: NonNullableFormBuilder,
        @Inject(MAT_DIALOG_DATA) private readonly dialogData: AdvancedOptions,
        private readonly commonDataService: CommonDataService,
        private readonly confirmationDialogService: ConfirmationDialogService,
        private readonly notificationMessageService: NotificationMessageService,
        private readonly translateService: TranslateService,
        private readonly openEstimateApiService: OpenEstimateApiService,
        private readonly dialogRef: MatDialogRef<SolPropertiesProposalAdvancedOptionsComponent>,
        public metadataService: CommonMetaDataService,
        private readonly commonFunctionsService: CommonFunctionsService,
        private readonly estimateCommonFunctionsService: EstimateCommonFunctionsService
    ) { }

    get proposalLink(): FormControl<string> {
        return this.advancedOptionsForm.controls.proposalLink as FormControl<string>;
    }

    get proposalIdControl(): FormControl<string> {
        return this.advancedOptionsForm.controls.proposalId as FormControl<string>;
    }
    ngOnInit(): void {
        this.areas = this.commonDataService.proposalAdvancedOptionsAreaList;
        this.initializeForm();
        this.updateUnlinkProposalState();

        this.isSDIApplicable = this.commonFunctionsService.isSdiApplicable();
    }
    
    initializeForm(): void {
        this.advancedOptions = this.dialogData;

        this.advancedOptionsForm = this.fb.group<IAdvancedOptionsFormGroup>({
            projectManagerLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomProjectManagerLaborText),
            projectManagerLabor: this.fb.control<string>(this.advancedOptions.projectManagerLaborText),
            engineerLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomEngineeringLaborText),
            engineerLabor: this.fb.control<string>(this.advancedOptions.engineeringLaborText),
            cyberSecurityEngineerLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomCENGLaborText),
            cyberSecurityEngineerLabor: this.fb.control<string>(this.advancedOptions.cengLaborText),
            energyEngineerLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomEENGLaborText),
            energyEngineerLabor: this.fb.control<string>(this.advancedOptions.eengLaborText),
            electricEngineerLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomLENGLaborText),
            electricEngineerLabor: this.fb.control<string>(this.advancedOptions.lengLaborText),
            networkEngineerLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomNENGLaborText),
            networkEngineerLabor: this.fb.control<string>(this.advancedOptions.nengLaborText),
            qualificationEngineerLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomQENGLaborText),
            qualificationEngineerLabor: this.fb.control<string>(this.advancedOptions.qengLaborText),
            graphicCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomGRAPLaborText),
            graphic: this.fb.control<string>(this.advancedOptions.grapLaborText),
            programmingCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomPROGLaborText),
            programming: this.fb.control<string>(this.advancedOptions.progLaborText),
            specialistLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomSpecialistLaborText),
            specialistLabor: this.fb.control<string>(this.advancedOptions.specialistLaborText),
            ddsCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomDDSLaborText),
            dds: this.fb.control<string>(this.advancedOptions.ddsLaborText),
            mechanicalLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomMechanicalLaborText),
            mechanicalLabor: this.fb.control<string>(this.advancedOptions.mechanicalLaborText),
            electricalLaborCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomElectricalLaborText),
            electricalLabor: this.fb.control<string>(this.advancedOptions.electricalLaborText),
            mechanicalFeeCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomMechanicalFeeText),
            mechanicalFee: this.fb.control<string>(this.advancedOptions.mechanicalFeesText),
            productivityReserveCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomProductivityReserveText),
            productivityReserve: this.fb.control<string>(this.advancedOptions.productivityReserveFeesText),
            technologyCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomTechFeesText),
            technology: this.fb.control<string>(this.advancedOptions.technologyFeesText),
            subcontractorFeeCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomSubcontractorFeesText),
            subcontractorFee: this.fb.control<string>(this.advancedOptions.subcontractorFeesText),
            riskCheckbox: this.fb.control<boolean>(this.advancedOptions.useCustomRiskText),
            risk: this.fb.control<string>(this.advancedOptions.riskText),
            distributeTopLevelCostsOption: this.fb.control<boolean>(this.advancedOptions.distributeTopLevel),
            distributedTopLevelOptions: this.fb.control<DistributeTopLevelOptions>(this.advancedOptions.distributeTopLevelOption),
            areaSelectionForTopLevelCosts: this.fb.control<string>(this.advancedOptions?.selectedAreaId || this.areas?.[CONSTANTS.ZERO]?.id || CONSTANTS.EMPTY_STRING),
            proposalLink: this.fb.control<string>(this.commonDataService.getEstimateData()?.proposalLink ?? ""),
            proposalId: this.fb.control<string>(this.commonDataService.getEstimateData()?.proposalID ?? '')
        });

        this.applyInitialDisabledStates();
    }

    private applyInitialDisabledStates(): void {
        this.onCheckboxToggle('projectManagerLaborCheckbox', 'projectManagerLabor');
        this.onCheckboxToggle('engineerLaborCheckbox', 'engineerLabor');
        this.onCheckboxToggle('cyberSecurityEngineerLaborCheckbox', 'cyberSecurityEngineerLabor');
        this.onCheckboxToggle('energyEngineerLaborCheckbox', 'energyEngineerLabor');
        this.onCheckboxToggle('electricEngineerLaborCheckbox', 'electricEngineerLabor');
        this.onCheckboxToggle('networkEngineerLaborCheckbox', 'networkEngineerLabor');
        this.onCheckboxToggle('qualificationEngineerLaborCheckbox', 'qualificationEngineerLabor');
        this.onCheckboxToggle('graphicCheckbox', 'graphic');
        this.onCheckboxToggle('programmingCheckbox', 'programming');
        this.onCheckboxToggle('specialistLaborCheckbox', 'specialistLabor');
        this.onCheckboxToggle('ddsCheckbox', 'dds');
        this.onCheckboxToggle('mechanicalLaborCheckbox', 'mechanicalLabor');
        this.onCheckboxToggle('electricalLaborCheckbox', 'electricalLabor');
        this.onCheckboxToggle('mechanicalFeeCheckbox', 'mechanicalFee');
        this.onCheckboxToggle('productivityReserveCheckbox', 'productivityReserve');
        this.onCheckboxToggle('technologyCheckbox', 'technology');
        this.onCheckboxToggle('subcontractorFeeCheckbox', 'subcontractorFee');
        this.onCheckboxToggle('riskCheckbox', 'risk');
        this.onDistributeTopLevelToggle();
    }

    onCheckboxToggle(
        checkboxKey: keyof IAdvancedOptionsFormGroup,
        inputKey: keyof IAdvancedOptionsFormGroup
    ): void {
        this.setControlStateByCheckbox(checkboxKey, inputKey);
    }

    onDistributeTopLevelToggle(): void {
        this.setControlStateByCheckbox('distributeTopLevelCostsOption', 'distributedTopLevelOptions');
        this.updateAreaSelectionState();
    }

    onDistributedTopLevelOptionToggle(): void {
        this.updateAreaSelectionState();
    }

    private setControlStateByCheckbox(
        checkboxKey: keyof IAdvancedOptionsFormGroup,
        inputKey: keyof IAdvancedOptionsFormGroup
    ): void {
        const checkbox = this.advancedOptionsForm.controls[checkboxKey] as FormControl<boolean>;
        const input = this.advancedOptionsForm.controls[inputKey];

        if (checkbox.value) {
            input.enable({ emitEvent: false });
            return;
        }

        input.disable({ emitEvent: false });
    }

    private updateAreaSelectionState(): void {
        const isDistributeTopLevelYesSelected = this.advancedOptionsForm.controls.distributeTopLevelCostsOption.value === true;
        const isMoveToAreaSelected = this.advancedOptionsForm.controls.distributedTopLevelOptions.value === DistributeTopLevelOptions.MoveToArea;
        const areaControl = this.advancedOptionsForm.controls.areaSelectionForTopLevelCosts;

        if (isDistributeTopLevelYesSelected && isMoveToAreaSelected) {
            areaControl.enable({ emitEvent: false });
            return;
        }

        areaControl.disable({ emitEvent: false });
    }

    trackByIndex(index: number): number {
        return index;
    }

    onUnlinkProposalClick(): void {
        this.confirmationDialogService.confirmWarning('UNLINK_PROPOSAL_CONFIRMATION')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((confirmed) => {
                if (!confirmed) {
                    return;
                }

                const estimateData = this.commonDataService.getEstimateData();
                const request: UnlinkProposalRequest = {
                    estimateId: this.commonDataService.getEstimateId(),
                    userId: this.commonDataService.getUserId(),
                    isCollaborativeEstimate: false,
                    estimateStatus: estimateData.estimateStatusId as EstimateStatus
                };

                this.openEstimateApiService.unlinkProposal(request)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => {
                            estimateData.proposalID = null;
                            estimateData.lastProposalGeneratedDate = null;
                            estimateData.proposalLink = null;
                            estimateData.proposalTaskID = null;
                            estimateData.sellPriceProposalTaskID = null;
                            this.proposalLink.setValue('');
                            this.proposalIdControl.setValue('');
                            this.updateUnlinkProposalState();
                        }
                    });
            });
    }

    private updateUnlinkProposalState(): void {
        const estimateData = this.commonDataService.getEstimateData();
        const isNotSubmitted = estimateData.estimateStatusId !== EstimateStatus.Submitted;
        const hasProposalId = !!estimateData.proposalID;
        this.isProposalCanBeUnlinked = isNotSubmitted && hasProposalId;
    }

    private readonly checkboxInputPairs: { checkbox: keyof IAdvancedOptionsFormGroup; input: keyof IAdvancedOptionsFormGroup; labelKey: string }[] = [
        { checkbox: 'projectManagerLaborCheckbox', input: 'projectManagerLabor', labelKey: 'PROPERTIES_PROPOSAL_ADVANCED_OPTIONS_PROJECT_MANAGER_LABOR' },
        { checkbox: 'engineerLaborCheckbox', input: 'engineerLabor', labelKey: 'PROPERTIES_PROPOSAL_ADVANCED_OPTIONS_ENGINEERING_LABOR' },
        { checkbox: 'cyberSecurityEngineerLaborCheckbox', input: 'cyberSecurityEngineerLabor', labelKey: 'CYBER_SECURITY_ENGINEER_LABOR' },
        { checkbox: 'energyEngineerLaborCheckbox', input: 'energyEngineerLabor', labelKey: 'ENERGY_ENGINEER_LABOR' },
        { checkbox: 'electricEngineerLaborCheckbox', input: 'electricEngineerLabor', labelKey: 'ELECTRICAL_ENGINEER_LABOR' },
        { checkbox: 'networkEngineerLaborCheckbox', input: 'networkEngineerLabor', labelKey: 'NETWORK_ENGINEER_LABOR' },
        { checkbox: 'qualificationEngineerLaborCheckbox', input: 'qualificationEngineerLabor', labelKey: 'QUALIFICATION_ENGINEER_LABOR' },
        { checkbox: 'graphicCheckbox', input: 'graphic', labelKey: 'GRAPHIC' },
        { checkbox: 'programmingCheckbox', input: 'programming', labelKey: 'PROGRAMMING' },
        { checkbox: 'specialistLaborCheckbox', input: 'specialistLabor', labelKey: 'PROPERTIES_PROPOSAL_ADVANCED_OPTIONS_SPECIALIST_LABOR' },
        { checkbox: 'ddsCheckbox', input: 'dds', labelKey: 'DDS' },
        { checkbox: 'mechanicalLaborCheckbox', input: 'mechanicalLabor', labelKey: 'PROPERTIES_PROPOSAL_ADVANCED_OPTIONS_MECHANICAL_LABOR' },
        { checkbox: 'electricalLaborCheckbox', input: 'electricalLabor', labelKey: 'PROPERTIES_PROPOSAL_ADVANCED_OPTIONS_ELECTRICAL_LABOR' },
        { checkbox: 'mechanicalFeeCheckbox', input: 'mechanicalFee', labelKey: 'MECHANICAL_FEE' },
        { checkbox: 'productivityReserveCheckbox', input: 'productivityReserve', labelKey: 'PRODUCTIVITY_RESERVE' },
        { checkbox: 'riskCheckbox', input: 'risk', labelKey: 'MENU_FILE_PROPERTIES_ESTIMATE_SETTINGS_RISK' },
        { checkbox: 'subcontractorFeeCheckbox', input: 'subcontractorFee', labelKey: 'PROPERTIES_PROPOSAL_ADVANCED_OPTIONS_SUBCONTRACTOR_FEE' },
        { checkbox: 'technologyCheckbox', input: 'technology', labelKey: 'PROPERTIES_PROPOSAL_ADVANCED_OPTIONS_TECHNOLOGY_FEE' },
    ];

    onOkClick(): void {
        for (const pair of this.checkboxInputPairs) {
            if (pair.checkbox === 'mechanicalFeeCheckbox' && !this.isMechanicalFeeVisible) {
                continue;
            }
            const checkboxControl = this.advancedOptionsForm.controls[pair.checkbox] as FormControl<boolean>;
            const inputControl = this.advancedOptionsForm.controls[pair.input] as FormControl<string>;

            if (checkboxControl.value && !inputControl.value?.trim()) {
                const pleaseEnter = this.translateService.instant('PLEASE_ENTER');
                const label = this.translateService.instant(pair.labelKey);
                const text = this.translateService.instant('TEXT');
                this.notificationMessageService.showWarning(`${pleaseEnter} ${label} ${text}`);
                return;
            }
        }
        this.updateControlsState();
        this.dialogRef.close(this.advancedOptionsForm.dirty);
    }
    
    private updateControlsState(): void {
        const formValues = this.advancedOptionsForm.getRawValue();
        this.advancedOptions.useCustomProjectManagerLaborText = formValues.projectManagerLaborCheckbox;
        this.advancedOptions.projectManagerLaborText = formValues.projectManagerLabor;
        this.advancedOptions.useCustomEngineeringLaborText = formValues.engineerLaborCheckbox;
        this.advancedOptions.engineeringLaborText = formValues.engineerLabor;
        this.advancedOptions.useCustomCENGLaborText = formValues.cyberSecurityEngineerLaborCheckbox;
        this.advancedOptions.cengLaborText = formValues.cyberSecurityEngineerLabor;
        this.advancedOptions.useCustomEENGLaborText = formValues.energyEngineerLaborCheckbox;
        this.advancedOptions.eengLaborText = formValues.energyEngineerLabor;
        this.advancedOptions.useCustomLENGLaborText = formValues.electricEngineerLaborCheckbox;
        this.advancedOptions.lengLaborText = formValues.electricEngineerLabor;
        this.advancedOptions.useCustomNENGLaborText = formValues.networkEngineerLaborCheckbox;
        this.advancedOptions.nengLaborText = formValues.networkEngineerLabor;
        this.advancedOptions.useCustomQENGLaborText = formValues.qualificationEngineerLaborCheckbox;
        this.advancedOptions.qengLaborText = formValues.qualificationEngineerLabor;
        this.advancedOptions.useCustomGRAPLaborText = formValues.graphicCheckbox;
        this.advancedOptions.grapLaborText = formValues.graphic;
        this.advancedOptions.useCustomPROGLaborText = formValues.programmingCheckbox;
        this.advancedOptions.progLaborText = formValues.programming;
        this.advancedOptions.useCustomSpecialistLaborText = formValues.specialistLaborCheckbox;
        this.advancedOptions.specialistLaborText = formValues.specialistLabor;
        this.advancedOptions.useCustomDDSLaborText = formValues.ddsCheckbox;
        this.advancedOptions.ddsLaborText = formValues.dds;
        this.advancedOptions.useCustomMechanicalLaborText = formValues.mechanicalLaborCheckbox;
        this.advancedOptions.mechanicalLaborText = formValues.mechanicalLabor;
        this.advancedOptions.useCustomElectricalLaborText = formValues.electricalLaborCheckbox;
        this.advancedOptions.electricalLaborText = formValues.electricalLabor;
        // Hidden for non-Mechanical estimates: leave the stored value untouched.
        if (this.isMechanicalFeeVisible) {
            this.advancedOptions.useCustomMechanicalFeeText = formValues.mechanicalFeeCheckbox;
            this.advancedOptions.mechanicalFeesText = formValues.mechanicalFee;
        }
        this.advancedOptions.useCustomProductivityReserveText = formValues.productivityReserveCheckbox;
        this.advancedOptions.productivityReserveFeesText = formValues.productivityReserve;
        this.advancedOptions.useCustomTechFeesText = formValues.technologyCheckbox;
        this.advancedOptions.technologyFeesText = formValues.technology;
        this.advancedOptions.useCustomSubcontractorFeesText = formValues.subcontractorFeeCheckbox;
        this.advancedOptions.subcontractorFeesText = formValues.subcontractorFee;
        this.advancedOptions.useCustomRiskText = formValues.riskCheckbox;
        this.advancedOptions.riskText = formValues.risk;
        this.advancedOptions.distributeTopLevel = formValues.distributeTopLevelCostsOption;
        this.advancedOptions.distributeTopLevelOption = formValues.distributedTopLevelOptions;
        this.advancedOptions.selectedAreaId = formValues.areaSelectionForTopLevelCosts;
    }

    get isSubcontractorFeeVisible(): boolean {
        return this.isSDIApplicable;
    }

    get isMechanicalFeeVisible(): boolean {
        return this.estimateCommonFunctionsService.isMechanicalFeeApplicable();
    }
}
  